import { useEffect, useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import { applyTranscriptionFeatureAvailability, DEFAULT_TRANSCRIPTION_SETTINGS, TranscriptionSettings, type TranscriptionFeatureAvailability } from "../components/TranscriptionSettings";
import type { BillingFeature, MediaAudioStream, TranscriptionBatch, TranscriptionSettings as Settings } from "../lib/api";
import { localizedError } from "../lib/localized-error";
import { isSupportedMediaFile, MEDIA_ACCEPT, MEDIA_FORMAT_NAMES } from "../media-formats";
import { useTasks } from "../tasks";

interface UploadState { file: File; progress: number; stage: string; error: string | null }
interface UploadLimits {
  code?: string;
  max_file_seconds: number;
  max_file_bytes: number;
  max_batch_items: number;
  queue_priority?: number;
}
interface TrackPrompt { file: File; streams: MediaAudioStream[]; selected: number; resolve: (streamIndex: number) => void }

export function DashboardPage() {
  const { t } = useTranslation();
  const { api, openAuth, session } = useAuth();
  const { prepend, tasks } = useTasks();
  const fileInput = useRef<HTMLInputElement>(null);
  const settingsMenu = useRef<HTMLDivElement>(null);
  const settingsToggle = useRef<HTMLButtonElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [settings, setSettings] = useState<Settings>(() => structuredClone(DEFAULT_TRANSCRIPTION_SETTINGS));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [activeBatch, setActiveBatch] = useState<TranscriptionBatch | null>(null);
  const [trackPrompt, setTrackPrompt] = useState<TrackPrompt | null>(null);
  const [source, setSource] = useState<"local" | "link" | "record">("local");
  const [sourceUrl, setSourceUrl] = useState("");
  const [linkStage, setLinkStage] = useState<"downloading" | "queued" | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [limits, setLimits] = useState<UploadLimits>({ max_file_seconds: 1800, max_file_bytes: 1024 ** 3, max_batch_items: 1 });
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [planCode, setPlanCode] = useState("guest");
  const [availability, setAvailability] = useState<TranscriptionFeatureAvailability | undefined>();
  const recorder = useRef<MediaRecorder | null>(null);
  const recordedChunks = useRef<Blob[]>([]);
  const uploadController = useRef<AbortController | null>(null);
  const [activeUpload, setActiveUpload] = useState<File | null>(null);

  useEffect(() => {
    if (!session) return;
    let active = true;
    void Promise.all([api.billingCatalog(), api.billingUsage()]).then(([catalog, usage]) => {
      const plans = (catalog.plans as UploadLimits[] | undefined) ?? [];
      const plan = plans.find((item) => item.code === usage.plan_code);
      if (active) {
        const nextAvailability = featureAvailability(usage.features);
        if (plan) setLimits(plan);
        setRemainingSeconds(Math.max(0, Number(usage.balance_seconds) || 0));
        setPlanCode(usage.plan_code);
        setAvailability(nextAvailability);
        setSettings((current) => applyTranscriptionFeatureAvailability(current, nextAvailability));
      }
    }).catch(() => undefined);
    return () => { active = false; };
  }, [api, session]);

  useEffect(() => {
    if (!settingsOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!settingsMenu.current?.contains(event.target as Node)) setSettingsOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setSettingsOpen(false);
      settingsToggle.current?.focus();
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [settingsOpen]);

  useEffect(() => {
    if (!activeBatch || ["completed", "partial", "failed"].includes(activeBatch.status)) return;
    const timer = window.setInterval(() => {
      void api.transcriptionBatch(activeBatch.id).then(setActiveBatch).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [activeBatch, api]);

  function chooseFiles(selected: FileList | null) {
    if (!selected) return;
    const candidates = [...selected];
    const unsupported = candidates.find((file) => !isSupportedMediaFile(file));
    const oversized = candidates.find((file) => file.size > limits.max_file_bytes);
    if (candidates.length > limits.max_batch_items || oversized || unsupported) {
      setInputError(unsupported
        ? t("dashboard.unsupportedFormat", { name: unsupported.name })
        : oversized
          ? t("dashboard.fileTooLarge", { name: oversized.name, size: formatBytes(limits.max_file_bytes) })
        : t("dashboard.tooManyFiles", { count: limits.max_batch_items }));
      return;
    }
    setInputError(null);
    setFiles(candidates);
    setUploads([]);
  }

  function drop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    chooseFiles(event.dataTransfer.files);
  }

  function chooseAudioStream(file: File, streams: MediaAudioStream[]): Promise<number> {
    return new Promise((resolve) => {
      setTrackPrompt({ file, streams, selected: streams[0]!.stream_index, resolve });
    });
  }

  function confirmAudioStream() {
    if (!trackPrompt) return;
    const { resolve, selected } = trackPrompt;
    setTrackPrompt(null);
    resolve(selected);
  }

  async function start() {
    if (!session) {
      openAuth();
      return;
    }
    if (source === "link") {
      let url: URL;
      try { url = new URL(sourceUrl.trim()); }
      catch { setInputError(t("dashboard.invalidLink")); return; }
      if (url.protocol !== "https:") { setInputError(t("dashboard.invalidLink")); return; }
      setBusy(true);
      setSettingsOpen(false);
      setInputError(null);
      try {
        const task = await api.importAndTranscribe(url.toString(), settings, setLinkStage);
        prepend(task);
        setSourceUrl("");
      } catch (reason) {
        setInputError(localizedError(reason, t));
      } finally {
        setBusy(false);
        setLinkStage(null);
      }
      return;
    }
    if (files.length === 0) {
      fileInput.current?.click();
      return;
    }
    setBusy(true);
    setSettingsOpen(false);
    setInputError(null);
    let batch: TranscriptionBatch | null = null;
    if (files.length > 1) {
      try {
        batch = await api.createTranscriptionBatch(files.length);
        setActiveBatch(batch);
      } catch (reason) {
        setInputError(localizedError(reason, t));
        setBusy(false);
        return;
      }
    } else {
      setActiveBatch(null);
    }
    const initial = files.map((file) => ({ file, progress: 0, stage: "queued", error: null }));
    setUploads(initial);
    try {
      for (const file of files) {
        const controller = new AbortController();
        uploadController.current = controller;
        setActiveUpload(file);
        try {
          const task = await api.uploadAndTranscribe(file, settings, (progress, stage) => {
            setUploads((current) => current.map((item) => item.file === file ? { ...item, progress, stage } : item));
          }, batch?.id, chooseAudioStream, controller.signal);
          prepend(task);
        } catch (reason) {
          const message = reason instanceof DOMException && reason.name === "AbortError"
            ? t("dashboard.uploadCanceled")
            : localizedError(reason, t);
          setUploads((current) => current.map((item) => item.file === file ? { ...item, error: message } : item));
        } finally {
          if (uploadController.current === controller) uploadController.current = null;
          setActiveUpload(null);
        }
      }
    } finally {
      if (batch) {
        try {
          setActiveBatch(await api.finishTranscriptionBatchUploads(batch.id));
        } catch (reason) {
          setInputError(localizedError(reason, t));
        }
      }
      setBusy(false);
    }
  }

  async function toggleRecording() {
    if (recorder.current?.state === "recording") {
      recorder.current.stop();
      return;
    }
    setRecordError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const active = new MediaRecorder(
        stream,
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? { mimeType: "audio/webm;codecs=opus" }
          : undefined,
      );
      recordedChunks.current = [];
      active.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) recordedChunks.current.push(event.data);
      });
      active.addEventListener("stop", () => {
        const blob = new Blob(recordedChunks.current, { type: active.mimeType || "audio/webm" });
        const timestamp = new Date().toISOString().replaceAll(/[:.]/gu, "-");
        setFiles([new File([blob], `recording-${timestamp}.webm`, { type: blob.type })]);
        setUploads([]);
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        recorder.current = null;
      });
      recorder.current = active;
      active.start(1000);
      setRecording(true);
    } catch (reason) {
      setRecordError(localizedError(reason, t));
      setRecording(false);
    }
  }

  const modeLabel = t(`settings.${settings.mode}`);
  const languageLabel = settings.language ?? t("source.auto");
  const repairLabel = t(`settings.${settings.audioRepairMode === "off" ? "repairOff" : settings.audioRepairMode === "denoise" ? "denoise" : "enhanceSpeech"}`);
  const quotaSummary = t("dashboard.quotaAvailable", { duration: formatLimitDuration(remainingSeconds) });

  return <div className="page dashboard-page">
    <header className="page-hero">
      <div><p className="eyebrow">{t("dashboard.eyebrow")}</p><h1>{t("dashboard.titleA")}<br /><span>{t("dashboard.titleB")}</span></h1></div>
      <div className="queue-note"><i /><p><strong>{t("dashboard.queueReady")}</strong><small>{t("dashboard.queueHint")}</small></p></div>
    </header>
    <div className="dashboard-grid">
      <article className="panel import-card">
        <header className="panel-heading"><div><p className="eyebrow">{t("dashboard.startWork")}</p><h2>{t("dashboard.importTitle")}</h2></div><span className="secure-note"><Icon name="check" />{t("dashboard.private")}</span></header>
        <div className="source-tabs" role="tablist">
          <button className={source === "local" ? "is-active" : ""} onClick={() => setSource("local")}><Icon name="upload" />{t("dashboard.local")}</button>
          <button className={source === "link" ? "is-active" : ""} onClick={() => setSource("link")}><Icon name="link" />{t("dashboard.link")}</button>
          <button className={source === "record" ? "is-active" : ""} onClick={() => setSource("record")}><Icon name="mic" />{t("dashboard.record")}</button>
        </div>
        <input ref={fileInput} className="visually-hidden" type="file" multiple accept={MEDIA_ACCEPT} onChange={(event) => chooseFiles(event.target.files)} />
        {source === "local" && <button type="button" className="drop-zone" onClick={() => fileInput.current?.click()} onDragOver={(event) => event.preventDefault()} onDrop={drop}>
          <span className="drop-icon"><Icon name="upload" /></span>
          <strong>{files.length ? t("dashboard.selected", { count: files.length }) : t("dashboard.drop")}</strong>
          <span>{files.length ? files.map((file) => file.name).join(" · ") : t("dashboard.choose")}</span>
          <small>{t("dashboard.formats", { formats: MEDIA_FORMAT_NAMES, duration: formatLimitDuration(limits.max_file_seconds), size: formatBytes(limits.max_file_bytes), batch: limits.max_batch_items })}</small>
        </button>}
        {source === "link" && <div className="source-entry"><Icon name="link" /><label><span>{t("dashboard.link")}</span><input type="url" value={sourceUrl} placeholder={t("dashboard.linkPlaceholder")} onChange={(event) => { setSourceUrl(event.target.value); setInputError(null); }} /></label><span className="source-entry__state">{linkStage ? t(`dashboard.${linkStage}`) : t("dashboard.linkHint")}</span></div>}
        {source === "record" && <div className="record-entry"><button type="button" className={`record-button${recording ? " is-recording" : ""}`} aria-label={recording ? t("dashboard.recordStop") : t("dashboard.recordStart")} onClick={() => void toggleRecording()}><Icon name={recording ? "close" : "mic"} /></button><strong>{recording ? t("dashboard.recording") : files[0]?.name ?? t("dashboard.recordStart")}</strong><small>{recording ? t("dashboard.recordStop") : t("dashboard.record")}</small>{recordError && <p className="form-error" role="alert">{recordError}</p>}</div>}
        <div className="import-facts" aria-label={t("dashboard.uploadFacts")}>
          <article><Icon name="clock" /><p><span>{t("dashboard.availableQuota")}</span><strong>{quotaSummary}</strong></p></article>
          <article><Icon name="chart" /><p><span>{t("dashboard.estimatedPriority")}</span><strong>{t(`dashboard.priority.${planCode}`, { defaultValue: t("dashboard.priority.standard") })}</strong></p></article>
          <article><Icon name="settings" /><p><span>{t("dashboard.accuracyFactors")}</span><strong>{t("dashboard.accuracySummary", { mode: modeLabel, language: languageLabel, repair: repairLabel })}</strong></p></article>
        </div>
        {uploads.length > 0 && <div className="upload-list">{uploads.map((item) => <div key={`${item.file.name}-${item.file.lastModified}`}><span className="file-glyph">{fileExtension(item.file.name)}</span><p><strong>{item.file.name}</strong><small>{item.error ?? t(`dashboard.stage.${item.stage}`, { defaultValue: item.stage })}</small></p><div className="progress-track"><i style={{ width: `${item.progress}%` }} /></div>{activeUpload === item.file && !item.error && <button type="button" className="upload-cancel" onClick={() => uploadController.current?.abort()}>{t("dashboard.cancelUpload")}</button>}</div>)}</div>}
        {activeBatch && <div className="batch-summary" aria-live="polite"><div><strong>{t("dashboard.batchProgress")}</strong><span>{t(`dashboard.batchStatus.${activeBatch.status}`)}</span></div><p>{t("dashboard.batchCounts", { completed: activeBatch.completed_items, failed: activeBatch.failed_items, total: activeBatch.total_items })}</p><div className="progress-track"><i style={{ width: `${Math.round(100 * (activeBatch.completed_items + activeBatch.failed_items) / activeBatch.total_items)}%` }} /></div></div>}
        {inputError && <p className="form-error" role="alert">{inputError}</p>}
        {session?.user == null && <button type="button" className="signin-strip" onClick={openAuth}><Icon name="check" /><span>{t("dashboard.signInRequired")}</span><strong>{t("dashboard.signIn")}</strong></button>}
        <footer className="import-foot">
          <p><i className="signal-dot" />{t("dashboard.modeSummary", { mode: modeLabel, language: languageLabel })}</p>
          <div className="split-action-menu" ref={settingsMenu}>
            <div className="split-action" role="group" aria-label={t("dashboard.start")}><button type="button" className="split-action__main" disabled={busy} onClick={() => void start()}>{busy ? t("common.loading") : t("dashboard.start")}</button><button ref={settingsToggle} type="button" className={settingsOpen ? "split-action__toggle is-open" : "split-action__toggle"} aria-label={t("dashboard.settings")} aria-controls="transcription-settings-popover" aria-expanded={settingsOpen} aria-haspopup="dialog" disabled={busy} onClick={() => setSettingsOpen((value) => !value)}><Icon name="chevron" /></button></div>
            <TranscriptionSettings open={settingsOpen} settings={settings} onChange={setSettings} onClose={() => setSettingsOpen(false)} onStart={() => void start()} busy={busy} quotaSummary={quotaSummary} availability={availability} />
          </div>
        </footer>
      </article>
      <aside className="panel signal-card">
        <header className="panel-heading"><div><p className="eyebrow">{t("dashboard.signal")}</p><h2>{t("dashboard.jobCount", { count: tasks.length })}</h2></div><Icon name="more" /></header>
        <div className="signal-orbit"><div><strong>{tasks.filter((task) => task.status === "completed").length}</strong><span>{t("status.completed")}</span></div></div>
        <div className="signal-list">{tasks.slice(0, 2).map((task) => <div key={task.id}><i className={`status-dot status-dot--${task.status}`} /><p><strong>{task.title}</strong><small>{taskStatusLabel(task, t)}</small></p></div>)}</div>
        <Link className="signal-link" to="/files">{t("dashboard.viewAll")} <Icon name="chevron" /></Link>
      </aside>
    </div>
    <section className="recent-section"><header className="section-heading"><div><p className="eyebrow">{t("dashboard.recent")}</p><h2>{t("dashboard.continue")}</h2></div><Link to="/files">{t("dashboard.viewAll")} <Icon name="chevron" /></Link></header><TaskTable tasks={tasks.slice(0, 4)} /></section>
    {trackPrompt && <div className="compact-dialog-backdrop"><section className="panel compact-dialog" role="dialog" aria-modal="true" aria-labelledby="audio-track-dialog-title"><header><Icon name="wave" /><h2 id="audio-track-dialog-title">{t("settings.trackDialogTitle")}</h2></header><p>{t("settings.trackDialogHint", { name: trackPrompt.file.name })}</p><label><span>{t("settings.track")}</span><select autoFocus value={trackPrompt.selected} onChange={(event) => setTrackPrompt((current) => current ? { ...current, selected: Number(event.target.value) } : null)}>{trackPrompt.streams.map((stream) => <option key={stream.stream_index} value={stream.stream_index}>{t("settings.trackOption", { index: stream.stream_index + 1, codec: stream.codec_name?.toUpperCase() ?? t("common.unknown"), language: stream.language ?? t("source.auto"), channels: stream.channels ?? "—" })}</option>)}</select></label><footer><button type="button" className="button button--primary" onClick={confirmAudioStream}>{t("settings.confirmTrackSelection")}</button></footer></section></div>}
  </div>;
}

function featureAvailability(features: BillingFeature[] | undefined): TranscriptionFeatureAvailability | undefined {
  if (!features) return undefined;
  const enabled = (code: string) => features.some((feature) => feature.feature_code === code && Number(feature.enabled) === 1);
  return { speakerDiarization: enabled("speaker_diarization"), audioEnhancement: enabled("audio_enhancement") };
}

export function TaskTable({ tasks }: { tasks: ReturnType<typeof useTasks>["tasks"] }) {
  const { t, i18n } = useTranslation();
  if (!tasks.length) return <div className="panel empty-state"><Icon name="wave" /><strong>{t("dashboard.noRecent")}</strong><span>{t("dashboard.noRecentHint")}</span></div>;
  return <div className="panel file-table"><div className="file-row file-row--head"><span>{t("library.file")}</span><span>{t("library.status")}</span><span>{t("library.language")}</span><span>{t("library.duration")}</span><span>{t("library.modified")}</span></div>{tasks.map((task) => <Link to={`/editor/${task.id}`} className="file-row" key={task.id}><span className="file-name"><i><Icon name={task.content_type?.startsWith("video/") ? "play" : "wave"} /></i><span><strong>{task.title}</strong><small>{task.tags.join(" · ") || t("source.local")}</small></span></span><span><em className={`status-pill status-pill--${task.status}`}>{taskStatusLabel(task, t)}</em></span><span>{task.detected_language ?? task.requested_language ?? t("common.unknown")}</span><span className="mono">{formatDuration(task.duration_ms)}</span><span>{new Intl.DateTimeFormat(i18n.language, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(task.updated_at_ms)}</span></Link>)}</div>;
}

export function taskStatusLabel(task: { status: string; processing_stage?: string | null }, t: ReturnType<typeof useTranslation>["t"]): string {
  const key = task.status === "completed" || task.status === "failed"
    ? task.status
    : task.processing_stage ?? task.status;
  return t(`status.${key}`, { defaultValue: key });
}

function fileExtension(name: string): string { return name.split(".").pop()?.slice(0, 4).toUpperCase() || "FILE"; }
export function formatDuration(duration: number | null): string { if (duration == null) return "—"; const seconds = Math.floor(duration / 1000); return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`; }
function formatBytes(bytes: number): string { return bytes >= 1024 ** 3 ? `${Math.round(bytes / 1024 ** 3)} GB` : `${Math.round(bytes / 1024 ** 2)} MB`; }
function formatLimitDuration(seconds: number): string { return seconds >= 3600 ? `${Math.round(seconds / 3600)} h` : `${Math.round(seconds / 60)} min`; }
